import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCOPE_WILDCARD, masterRoleId } from '../../lib/constants';
import { inviteUser } from '../../lib/identityRegistry';
import type { AssignedInstance, FacilityRecord, RoleRecord } from '../../types/registry';
import { AssignFacilityDialog } from './AssignFacilityDialog';

interface InviteUserFormProps {
  roles: RoleRecord[];
  facilities: FacilityRecord[];
}

function areaSummary(facility: FacilityRecord | undefined, areaIds: string[]): string {
  if (areaIds.includes(SCOPE_WILDCARD)) return 'All areas';
  const names = (facility?.area_ids ?? [])
    .filter((area) => areaIds.includes(area.area_id))
    .map((area) => area.name);
  return names.length ? names.join(', ') : 'All areas';
}

export function InviteUserForm({ roles, facilities }: InviteUserFormProps): JSX.Element {
  const queryClient = useQueryClient();
  // Only the per-client master role is non-assignable; all other roles (incl. viewer) are allowed.
  const assignableRoles = useMemo(
    () => roles.filter((entry) => entry.role_id !== masterRoleId(entry.client_id)),
    [roles],
  );
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(assignableRoles[0]?.role_id ?? '');
  const [selectedFacilities, setSelectedFacilities] = useState<Record<string, string[]>>({});
  const [addingFacility, setAddingFacility] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assignedInstances = useMemo<AssignedInstance[]>(
    () =>
      Object.entries(selectedFacilities).map(([facilityId, areaIds]) => ({
        facility_id: facilityId,
        area_ids: areaIds.length ? areaIds : [SCOPE_WILDCARD],
      })),
    [selectedFacilities],
  );

  const mutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setMessage(`Invited ${email} as ${result.role}.`);
      setEmail('');
      setSelectedFacilities({});
      setError(null);
    },
    onError: (mutationError) => {
      setMessage(null);
      setError(mutationError instanceof Error ? mutationError.message : 'Invite failed');
    },
  });

  function removeFacility(facilityId: string) {
    setSelectedFacilities((current) => {
      const next = { ...current };
      delete next[facilityId];
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    mutation.mutate({
      email,
      role,
      assigned_instances: assignedInstances,
    });
  }

  const assignedEntries = Object.entries(selectedFacilities);

  return (
    <>
      <form className="invite-form" onSubmit={handleSubmit}>
        <label className="form-field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="form-field">
          <span>Role</span>
          <select value={role} onChange={(event) => setRole(event.target.value)} required>
            {assignableRoles.map((entry) => (
              <option key={entry.role_id} value={entry.role_id}>
                {entry.role_name}
              </option>
            ))}
          </select>
        </label>

        <div className="facility-picker">
          <span>Facility scope</span>
          {!assignedEntries.length ? <p className="hint-text">No facilities assigned yet.</p> : null}
          {assignedEntries.map(([facilityId, areaIds]) => {
            const facility = facilities.find((entry) => entry.facility_id === facilityId);
            return (
              <div key={facilityId} className="facility-block">
                <p className="facility-block__title">{facility?.name ?? facilityId}</p>
                <p className="hint-text">{areaSummary(facility, areaIds)}</p>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() => removeFacility(facilityId)}
                >
                  Remove
                </button>
              </div>
            );
          })}
          <button className="secondary-button" type="button" onClick={() => setAddingFacility(true)}>
            Add facility
          </button>
        </div>

        {message ? <p className="hint-text">{message}</p> : null}
        {error ? <div className="form-error">{error}</div> : null}

        <button className="primary-button" disabled={mutation.isPending} type="submit">
          {mutation.isPending ? 'Sending invite...' : 'Send invite'}
        </button>
      </form>

      {addingFacility ? (
        <AssignFacilityDialog
          facilities={facilities}
          assignedFacilityIds={Object.keys(selectedFacilities)}
          onAdd={(facilityId, areaIds) => {
            setSelectedFacilities((current) => ({ ...current, [facilityId]: areaIds }));
            setAddingFacility(false);
          }}
          onCancel={() => setAddingFacility(false)}
        />
      ) : null}
    </>
  );
}
